import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { 
  LayoutDashboard, 
  FileText, 
  Users, 
  LogOut, 
  Menu,
  X,
  BarChart3,
  Download
} from "lucide-react";
import { Button } from "@/components/ui/button";
import NotificationCenter from "@/components/NotificationCenter";
import PushNotificationToggle from "@/components/PushNotificationToggle";

const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Base nav items for all admins
  const baseNavItems = [
    { path: "/admin", label: "Dashboard", icon: LayoutDashboard },
    { path: "/admin/analytics", label: "Analytics", icon: BarChart3 },
    { path: "/admin/submissions", label: "Submissions", icon: FileText },
  ];

  // Add Users link only for owner
  const navItems = user?.is_owner 
    ? [...baseNavItems, { path: "/admin/users", label: "Users", icon: Users }, { path: "/admin/recovery", label: "Recovery", icon: Download }]
    : baseNavItems;

  const isActive = (path) => {
    if (path === "/admin") return location.pathname === "/admin";
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-white flex" data-testid="admin-layout">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-[#00B4E5] text-white
        transform transition-transform duration-200
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
      `}>
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <img 
                  src="/logo.png" 
                  alt="Sound of Good Times" 
                  className="w-32 mb-2"
                />
                <p className="text-xs text-white/60 mt-1">Agency Portal</p>
              </div>
              <button 
                className="lg:hidden text-white/70 hover:text-white"
                onClick={() => setSidebarOpen(false)}
              >
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-6">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 text-white/70 hover:text-white hover:bg-white/10 transition-colors duration-200 ${isActive(item.path) ? "text-white bg-white/20 border-l-4 border-[#F58220]" : ""}`}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon size={20} />
                <span className="font-medium">{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* Push notification toggle */}
          <div className="px-4 py-3 border-t border-white/20">
            <PushNotificationToggle />
          </div>

          {/* User info & logout */}
          <div className="p-4 border-t border-white/20">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#F58220] flex items-center justify-center font-heading text-black">
                {user?.name?.charAt(0) || "A"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{user?.name}</p>
                <p className="text-xs text-white/60 truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut size={18} className="mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Header with notifications */}
        <header className="bg-white border-b border-gray-200 px-4 lg:px-8 py-3 flex items-center justify-between">
          <button 
            className="lg:hidden text-gray-600 hover:text-black"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu size={24} />
          </button>
          <h1 className="lg:hidden font-heading text-lg text-[#00B4E5]">GOOD TIMES</h1>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-4">
            <NotificationCenter />
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6 lg:p-8 bg-gray-50">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
